import { useLocation } from "react-router-dom";
import Hero from "../../components/tickets/heroImage";
import InfoCards from "../../components/tickets/infoCards";
import TicketTypes from "../../components/tickets/ticketTypes";
import MapSection from "../../components/tickets/map";
import Artists from "../../components/tickets/artists";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import SectionDivider from "../../components/tickets/divider";
import { useEffect, useState } from "react";
import PageLoader from "../../components/pageLoader";
import { getStoreEventsById } from "../../api/ticketsPlatform/events";
import { useParams } from "react-router-dom";
import Navbar from "../../components/tickets/navBar";
import Footer from "../../components/tickets/footer";
import CookieBanner from "../../components/tickets/cookieBanner";
import { usePixel } from "../../hooks/usePixel";
import PublicTablesSection from "../../components/tickets/tableTypes";
import { useTranslation } from "react-i18next";

export default function EventDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const eventFromState = location.state;
  const { id } = useParams();
  const navigate = useNavigate();

  const eventId = eventFromState?.id || id;

  const [eventDetails, setEventDetails] = useState([]);

  const [loading, setLoading] = useState(true);

  const [activeTab, setActiveTab] = useState(
    location.state?.defaultTab || "tickets",
  );

  usePixel(eventDetails?.pixels || []);

  const fetchEventData = async () => {
    try {
      setLoading(true);

      const data = await getStoreEventsById(eventId);
      setEventDetails(data || []);
    } catch (err) {
      // err
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.BrevoConversationsID = "69d6c076b5525827090a16e7";
    window.BrevoConversations =
      window.BrevoConversations ||
      function () {
        (window.BrevoConversations.q = window.BrevoConversations.q || []).push(
          arguments,
        );
      };
    const s = document.createElement("script");
    s.async = true;
    s.src = "https://conversations-widget.brevo.com/brevo-conversations.js";
    document.head.appendChild(s);

    return () => {
      // cleanup if needed
      document.head.removeChild(s);
    };
  }, []);

  useEffect(() => {
    if (location.state?.defaultTab) {
      setActiveTab(location.state.defaultTab);
    }
  }, [location.state?.defaultTab]);

  useEffect(() => {
    fetchEventData();
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="bg-[#021610] text-white min-h-screen">
      <Navbar />
      <Hero event={eventDetails} />
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <button
          onClick={() => {
            navigate("/tickets-store/");
          }}
          className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {t("eventDetails.backToEvents")}
        </button>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <InfoCards event={eventDetails} />
        <MapSection event={eventDetails} />
        <SectionDivider />
        <Artists artists={eventDetails.event_artists} />
        <SectionDivider />
        <div className="w-full max-w-4xl mx-auto mt-12 mb-6">
          <div className="flex items-center w-full text-xs text-green-400/70">
            <Step number="1" label={t("eventDetails.stepTicketType")} active />
            <Line />
            <Step number="2" label={t("eventDetails.stepConfirm")} />
          </div>
        </div>
        {eventDetails?.is_tables_live ? (
          <>
            <div className="flex bg-[#031c14] border border-green-900/30 rounded-xl p-1 w-full">
              <TabButton
                label={t("eventDetails.tabTickets")}
                active={activeTab === "tickets"}
                onClick={() => setActiveTab("tickets")}
              />
              <TabButton
                label={t("eventDetails.tabTables")}
                active={activeTab === "tables"}
                onClick={() => setActiveTab("tables")}
              />
            </div>

            {activeTab === "tickets" && <TicketTypes event={eventDetails} />}
            {activeTab === "tables" && (
              <PublicTablesSection
                event={eventDetails}
                onSwitchToTickets={() => setActiveTab("tickets")}
              />
            )}
          </>
        ) : (
          <TicketTypes event={eventDetails} />
        )}
        {eventDetails.description && (
          <>
            <SectionDivider />
            <div className="mt-4 mb-6">
              <p className="text-[10px] tracking-widest text-green-400/90 uppercase mb-2">
                {t("eventDetails.aboutEvent")}
              </p>
              <div
                className="text-sm text-gray-300 leading-relaxed event-description max-w-full overflow-hidden break-words"
                dangerouslySetInnerHTML={{ __html: eventDetails.description }}
              />
            </div>
          </>
        )}
        {/* ORGANISER DETAILS */}
        {eventDetails.show_organiser_details && (
          <>
            <SectionDivider />
            <div className="mt-4 mb-6">
              <p className="text-[10px] tracking-widest text-green-400/90 uppercase mb-4">
                {t("eventDetails.organiser")}
              </p>
              <div className="bg-[#031c14] border border-green-900/30 rounded-xl p-5 flex justify-center items-start gap-4">
                {eventDetails.organiser_logo && (
                  <div className="w-16 h-16 rounded-xl mt-1 overflow-hidden border border-green-900/30 flex-shrink-0 bg-[#021610] flex items-center justify-center">
                    <img
                      src={eventDetails.organiser_logo}
                      alt={eventDetails.organiser_name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}

                <div className="flex-1 space-y-1.5">
                  {eventDetails.organiser_name && (
                    <p className="font-semibold text-white">
                      {eventDetails.organiser_name}
                    </p>
                  )}
                  {eventDetails.organiser_company_name && (
                    <p className="text-sm text-gray-400">
                      {eventDetails.organiser_company_name}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1">
                    {eventDetails.organiser_company_reg_no && (
                      <p className="text-[10px] tracking-widest text-gray-500 uppercase">
                        Reg. {eventDetails.organiser_company_reg_no}
                      </p>
                    )}
                    {eventDetails.organiser_company_reg_no && (
                      <p className="text-[10px] tracking-widest text-gray-500 uppercase">
                        |
                      </p>
                    )}
                    {eventDetails.organiser_vat_no && (
                      <p className="text-[10px] tracking-widest text-gray-500 uppercase">
                        VAT. {eventDetails.organiser_vat_no}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <CookieBanner />
      <Footer />
    </div>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 rounded-lg text-sm font-medium tracking-widest uppercase transition
        ${
          active ? "bg-green-500 text-black" : "text-gray-400 hover:text-white"
        }`}
    >
      {label}
    </button>
  );
}

function Line() {
  return <div className="flex-1 h-px bg-green-900/30 mx-4" />;
}

function Step({ number, label, active }) {
  return (
    <div className="flex items-center gap-3 shrink-0">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs
          ${
            active
              ? "bg-green-500 text-black"
              : "border border-green-500/30 text-green-400"
          }`}
      >
        {number}
      </div>
      <span className="hidden md:block whitespace-nowrap">{label}</span>
    </div>
  );
}
